import type { ReactNode } from "react";
import { SovraSkeleton } from "../../../components/skeleton/sovraSkeleton";

export type HomeSectionStatus = "loading" | "ready" | "error";

type HomeSectionHeaderProps = {
    title: string;
    titleId?: string;
    subtitle?: string;
    action?: ReactNode;
    strong?: boolean;
};

export const HomeSectionHeader = ({ title, titleId, subtitle, action, strong }: HomeSectionHeaderProps) => (
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div className="min-w-0">
            <h2 id={titleId} className="fs-5 fw-medium mb-1" style={{ lineHeight: "30px" }}>{title}</h2>
            {subtitle && <p className={`fs-8 text-secondary mb-0 ${strong ? "fw-medium" : ""}`}>{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
    </div>
);

type HomeSectionBodyProps = {
    status: HomeSectionStatus;
    hasContent: boolean;
    loadingLabel: string;
    emptyLabel: string;
    errorLabel: string;
    skeletonCount?: number;
    children: ReactNode;
};

export const HomeSectionBody = ({
    status,
    hasContent,
    loadingLabel,
    emptyLabel,
    errorLabel,
    skeletonCount = 2,
    children
}: HomeSectionBodyProps) => {
    if (status === "loading") {
        return (
            <div className="d-flex flex-column gap-3" aria-busy="true" aria-live="polite">
                <span className="visually-hidden">{loadingLabel}</span>
                {Array.from({ length: skeletonCount }, (_, index) => (
                    <SovraSkeleton key={index} shape="rectangle" style={{ width: "100%", height: 112 }} className="rounded-3" />
                ))}
            </div>
        );
    }

    if (status === "error") {
        return <p className="mb-0 small text-danger">{errorLabel}</p>;
    }

    if (!hasContent) {
        return <p className="mb-0 small text-secondary">{emptyLabel}</p>;
    }

    return children;
};
